/**
 * Agent Escalations & Suspension — shared API client (landlord).
 *
 * Single source of truth for the escalation-queue + per-case suspension endpoints,
 * consumed by AgentEscalationQueuePage, the shared CaseEscalationSection (Lead detail's
 * Overview tab AND Repair Detail), and RepairDetailPage. Replaces the two duplicated
 * local `fetch` helpers this pass removes (AgentEscalationQueuePage's
 * `escalationQueueFetch`, OverviewTab's `caseEscalationFetch`).
 *
 * Response types are DERIVED from the boundary contract in `@resida/api-types/agent` —
 * the same Zod schemas apps/api registers on these routes — so any field-name drift is a
 * typecheck error rather than a runtime `undefined`.
 *
 * Covers:
 *   GET  /landlord/api/agent-escalations
 *   GET  /landlord/api/cases/:id/agent-escalation
 *   POST /landlord/api/agent-escalations/:id/answer
 *   GET  /landlord/api/cases/:id/agent-suspension
 *   POST /landlord/api/cases/:id/agent-suspension/resume
 *   POST /landlord/api/cases/:id/agent-suspension/pause (RA-PAUSE-CONTROL, PR #1191)
 *
 * Dismiss is REMOVED (BR-ESC-RESOLVE) — answering is the only resolution path; the
 * former POST .../:id/dismiss endpoint now 404s for any caller, so no client method
 * exists for it.
 *
 * Errors: propagated as `apiFetch`'s `ApiClientError`, which carries the response
 * `status` — callers rely on this for the 409/404 "already resolved" lost-race case
 * (RA-ESC-CARD).
 */

import { apiFetch } from '../lib/api';
import type {
  EscalationListItem,
  EscalationListResponse,
  EscalationResolveResponse,
  CaseOpenEscalationResponse,
  SuspensionStateResponse,
  SuspensionResumeResponse,
} from '@resida/api-types/agent';

export async function fetchEscalationQueue(): Promise<EscalationListResponse> {
  return apiFetch<EscalationListResponse>('/landlord/api/agent-escalations');
}

export async function fetchOpenEscalationForCase(caseId: string): Promise<EscalationListItem | null> {
  const data = await apiFetch<CaseOpenEscalationResponse>(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-escalation`
  );
  return data.escalation;
}

export async function answerEscalation(id: string, answer: string): Promise<EscalationResolveResponse> {
  return apiFetch<EscalationResolveResponse>(
    `/landlord/api/agent-escalations/${encodeURIComponent(id)}/answer`,
    { method: 'POST', body: JSON.stringify({ answer }) }
  );
}

export async function fetchAgentSuspension(caseId: string): Promise<SuspensionStateResponse> {
  return apiFetch<SuspensionStateResponse>(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-suspension`
  );
}

export async function handBackAgent(caseId: string): Promise<SuspensionResumeResponse> {
  return apiFetch<SuspensionResumeResponse>(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-suspension/resume`,
    { method: 'POST' }
  );
}

/** RA-PAUSE-CONTROL: landlord-initiated manual pause, no TTL (PR #1191). */
export async function pauseAgent(caseId: string): Promise<SuspensionStateResponse> {
  return apiFetch<SuspensionStateResponse>(
    `/landlord/api/cases/${encodeURIComponent(caseId)}/agent-suspension/pause`,
    { method: 'POST' }
  );
}
